import React, { useCallback, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import styled from 'styled-components';

import { Routes } from 'routes';
import { isAppleIOS, getScreenSize, setStyleProperty, isScreenPortrait } from 'utils';
import { NormalizedStyles } from './normalize.css';
import { ErrorEngine } from 'components/containers';
import { selectStartRecordingUserSession } from 'redux/root.selectors';
import {
  useTheme,
  useIosOrientation,
  FullScreenProvider
} from 'hooks';
import { ReactComponent as IcnScrollDown } from 'assets/svgs/icn-scroll-down.svg';
import {
  TRANSLATIONS_VALUES_KEYS
} from 'redux/internationalization/internationalization';
import PdfLibLoader from 'components/partials/pdf-viewer/pdf-lib-loader.component';
import MicrosoftClarity from 'components/partials/microsoft-clarity/microsoft-clarity.component';
import { useTranslate } from 'services';

const { accessibility } = TRANSLATIONS_VALUES_KEYS;

const ScrollDownToHideAddressBar = styled.div`
  display: block;
  width: 100vw;
  height: var(--page-margin-top);
  visibility: var(--scroll-instruction-visibility);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  align-content: center;

  & > div {
    padding-bottom: 0.3rem;
  }
`;

const App: React.FC = () => {
  const { styles } = useTheme();
  const { isIOS } = useIosOrientation();

  const startRecordingUserSession = useSelector(selectStartRecordingUserSession);

  const [screenSize, setScreenSize] = useState<null | { width: number, height: number }>(null);

  const handle = useFullScreenHandle();
  const isFullScreen = handle.active;

  const { translate } = useTranslate();

  const handleResize = () => {
    const newScreenSize = getScreenSize();
    setScreenSize(newScreenSize);
  };

  // NOTE:  Going fullscreen 100vh calculates in also toolbar height which we dont want,
  //        because it shifts elements out of visible area. When going full screen we manually set height
  //        Ideal would be trigger layout repaint, but could not get it to work.
  useEffect(() => {
    if (isFullScreen && screenSize) {
      const height = isScreenPortrait()
        ? screenSize.width
        : screenSize.height;

      setStyleProperty('--view-height', `${height}px`);
    } else if (window.visualViewport) {
      // Before setting view height to 100vh, we attempt to use visualViewport
      let height = window.visualViewport.height;

      // Add top safe area to height on iOS in landscape (web page is rendered in the safe area, nothing is covering it)
      // In iOS portrait, we dont want to add safe area to height, because the address bar (minified version) is always visible
      if (isAppleIOS() && !isScreenPortrait()) {
        const safeAreaInsetTop = parseInt(
          getComputedStyle(document.documentElement).getPropertyValue('--sat') || '0',
          10
        );
        height += safeAreaInsetTop;
      }

      setStyleProperty('--view-height', `${height}px`);
    } else {
      setStyleProperty('--view-height', '100vh');
    }
  }, [isFullScreen, screenSize]);

  useEffect(() => {
    // Call handleResize immediately for initial screen size
    handleResize();

    window.addEventListener('resize', handleResize);

    if (window.screen.orientation) {
      window.screen.orientation.addEventListener('change', handleResize);
    } else {
      window.addEventListener('orientationchange', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);

      if (window.screen.orientation) {
        window.screen.orientation.removeEventListener('change', handleResize);
      } else {
        window.removeEventListener('orientationchange', handleResize);
      }
    };
  }, []);

  const toggleFullScreen = useCallback((forceExit: boolean = false, forceEnter: boolean = false) => {
    if (isAppleIOS()) return;
    if (forceExit && !isFullScreen) return;
    if (forceEnter && isFullScreen) return;

    isFullScreen
      ? handle.exit()
      : handle.enter();
  }, [handle, isFullScreen]);

  return (
    <FullScreenProvider toggleFullScreen={toggleFullScreen}>
      <ErrorEngine>
        {screenSize && (
          <>
            <NormalizedStyles
              generalColor={styles.colorParagraph}
              headingColor={styles.colorHeading}
              isIOS={isIOS}
              backgroundColor={styles.colorBackgroundBase}
            />
            <FullScreen className="smartscan-fullscreen" handle={handle}>
              <ScrollDownToHideAddressBar
                data-testid="hide-address-bar"
                style={{
                  backgroundColor: styles.colorPrimaryButtonBackground,
                  color: styles.colorPrimaryButtonText
                }}
              >
                <div>
                  { translate(accessibility.scroll_bottom_text) }
                </div>
                <IcnScrollDown />
              </ScrollDownToHideAddressBar>
              <Routes screenSize={screenSize} />
              <MicrosoftClarity start={startRecordingUserSession} />
              <PdfLibLoader />
            </FullScreen>
          </>
        )}
      </ErrorEngine>
    </FullScreenProvider>
  );
};

export default App;
